/**
 * Typdefinitioner för Heatmap-vyn
 */

import { Vehicle } from '@/services/vehicles/vehiclesService';

export type HeatmapMode =
  | 'all'
  | 'overspeed'
  | 'idling'
  | 'parking'
  | 'eco'
  | 'timepattern'
  | 'geofence'
  | 'incidents'
  | 'charging'
  | 'riskscore';

export type TimePattern = 'all' | 'morning' | 'day' | 'evening' | 'night';

export interface HeatmapProps {
  sessionId?: string;
  isImport?: boolean;
}

// Mode information for the info modal
export interface ModeInfo {
  titleKey: string;
  descriptionKey: string;
  fallbackTitle: string;
  fallbackDescription: string;
}

export const HEATMAP_MODE_INFO: Record<HeatmapMode, ModeInfo> = {
  all: {
    titleKey: 'heatmap.modeAll',
    descriptionKey: 'heatmap.infoAll',
    fallbackTitle: 'Alla punkter',
    fallbackDescription:
      'Visar alla GPS-punkter från valda fordon under vald tidsperiod. Intensitet baseras på antal punkter per område.',
  },
  overspeed: {
    titleKey: 'heatmap.modeOverspeed',
    descriptionKey: 'heatmap.infoOverspeed',
    fallbackTitle: 'Hastighetsöverträdelser',
    fallbackDescription:
      'Visar var fordon har överskridit hastighetsbegränsningen. Intensitet baseras på antal överträdelser och max överskridande.',
  },
  idling: {
    titleKey: 'heatmap.modeIdling',
    descriptionKey: 'heatmap.infoIdling',
    fallbackTitle: 'Idling/Tomgång',
    fallbackDescription:
      'Visar var fordon har stått stilla med tändning på. Intensitet baseras på total idling-tid per plats.',
  },
  parking: {
    titleKey: 'heatmap.modeParking',
    descriptionKey: 'heatmap.infoParking',
    fallbackTitle: 'Parkeringar',
    fallbackDescription:
      'Visar var resor avslutas (parkeringar). Varje nål representerar en parkeringsplats med 100 meter radie. Färg baseras på antal gånger man parkerat: Grön (1-2), Gul (3-5), Orange (6-10), Röd (11+).',
  },
  eco: {
    titleKey: 'heatmap.modeEco',
    descriptionKey: 'heatmap.infoEco',
    fallbackTitle: 'ECO-händelser',
    fallbackDescription:
      'Visar hård körning (harsh acceleration, braking, cornering). Intensitet baseras på antal händelser och allvarlighetsgrad.',
  },
  timepattern: {
    titleKey: 'heatmap.modeTimePattern',
    descriptionKey: 'heatmap.infoTimePattern',
    fallbackTitle: 'Tidsbaserade mönster',
    fallbackDescription:
      'Visar trafikmönster baserat på tid på dygnet. Välj morgon, dag, kväll eller natt för att se när fordon är mest aktiva.',
  },
  geofence: {
    titleKey: 'heatmap.modeGeofence',
    descriptionKey: 'heatmap.infoGeofence',
    fallbackTitle: 'Geofence-överträdelser',
    fallbackDescription:
      'Visar var fordon har lämnat eller kommit in i geofence-områden. Intensitet baseras på antal överträdelser.',
  },
  incidents: {
    titleKey: 'heatmap.modeIncidents',
    descriptionKey: 'heatmap.infoIncidents',
    fallbackTitle: 'Olyckor/Incidenter',
    fallbackDescription:
      'Visar allvarliga händelser som paniklarm, olyckor eller kraschar. Intensitet baseras på allvarlighetsgrad och antal incidenter.',
  },
  charging: {
    titleKey: 'heatmap.modeCharging',
    descriptionKey: 'heatmap.infoCharging',
    fallbackTitle: 'Laddningsstationer',
    fallbackDescription:
      'Visar var fordon har laddats (EV). Baseras på långa stopp (minst 5 minuter). Intensitet baseras på antal laddningar och unika fordon.',
  },
  riskscore: {
    titleKey: 'heatmap.modeRiskScore',
    descriptionKey: 'heatmap.infoRiskScore',
    fallbackTitle: 'Kombinerad risk-score',
    fallbackDescription:
      'Kombinerar flera riskfaktorer (hastighetsöverträdelser, ECO-händelser, incidenter) för att visa områden med hög risk. Intensitet baseras på kombinerad risk-score.',
  },
};

// Convert allVehicles to the format expected by SearchableVehicleSelect
export function mapVehicleForSelect(
  vehicle: Vehicle,
  isImport: boolean
): { id: string; registrationNumber: string; brand: string; model: string } {
  return {
    id: isImport ? vehicle.registration_number : vehicle.id?.toString() || '',
    registrationNumber: vehicle.registration_number,
    brand: vehicle.make || '',
    model: vehicle.model || '',
  };
}

// Get default date range (last 7 days)
export function getDefaultDateRange(): { fromDate: string; toDate: string } {
  const toDate = new Date().toISOString().split('T')[0];
  const fromDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];
  return { fromDate, toDate };
}
